import React, { useState, useEffect } from "react";
import { Formik, Form as FormikForm, Field, FormikProps } from "formik";
import messages from "./PageViewProfile.intl";
import { injectIntl } from "react-intl";
import { styled } from "@material-ui/core/styles";
import TextInput from "../../text-input";
import InputErrorMessage from "../../input-error-message";
import { required } from "../../form-utils";
import Button from "../../button";
import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client";
import { Grid, Chip, Card, Box, Button as MuiButton } from "@material-ui/core";
import SnackBar from "../../snackbar";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import EmailIcon from "@material-ui/icons/Email";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import SecurityIcon from "@material-ui/icons/Security";
import BusinessIcon from "@material-ui/icons/Business";
import SettingsIcon from "@material-ui/icons/Settings";
import { UserAvatarUploader } from "../../atoms/";
import useAuthContext from "../../../hooks/useAuthContext";
import { actions } from "../../auth-context-provider";
import { IStyledArguments } from "../../../types/styled-arguments";
import useIsManager from "../../../hooks/useIsManager";
import { navigate } from "@reach/router";

interface IFormValues {
  name: string;
  surname: string;
  email: string;
  role: string;
  password: string;
  passwordConfirmation: string;
}

const PageContainer = styled("div")(({ theme }: IStyledArguments) => {
  const { md } = theme.breakpoints.values;

  return {
    minHeight: "100vh",
    width: "100%",
    background: theme.palette.extra.backgroundGrey,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingBottom: theme.spacing(6),
    [`@media (max-width: ${md}px)`]: {
      padding: theme.spacing(2),
      paddingBottom: theme.spacing(4),
    },
  };
});

const ContentCard = styled(Card)(({ theme }: IStyledArguments) => {
  const { md } = theme.breakpoints.values;

  return {
    maxWidth: 600,
    width: "100%",
    background: theme.palette.background.paper,
    borderRadius: 20,
    border: `1px solid ${theme.palette.extra.borderColor}`,
    boxShadow: "0 4px 16px rgba(16, 21, 26, 0.08)",
    marginTop: theme.spacing(3),
    transition: "all 0.3s ease",
    paddingBottom: theme.spacing(5), // Added extra padding to bottom for logout button
    [`@media (max-width: ${md}px)`]: {
      borderRadius: 16,
      marginTop: theme.spacing(2),
      paddingBottom: theme.spacing(4), // Mobile extra padding
    },
  };
});

const ProfileHeader = styled(Box)(({ theme }: IStyledArguments) => {
  return {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(4, 3, 3, 3),
    background: theme.palette.extra.accentSoft,
    borderRadius: "20px 20px 0 0",
  };
});

const AvatarWrapper = styled("div")(({ theme }: IStyledArguments) => {
  return {
    position: "relative",
    marginBottom: theme.spacing(2),
    "& > div": {
      width: 120,
      height: 120,
      fontSize: "3.5rem",
      border: "4px solid #fff",
      boxShadow: "0 4px 16px rgba(16, 21, 26, 0.16)",
    },
  };
});

const UserName = styled("div")(({ theme }: IStyledArguments) => {
  return {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.5),
    textAlign: "center",
  };
});

const UserEmail = styled("div")(({ theme }: IStyledArguments) => {
  return {
    fontSize: "1rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    "& svg": {
      fontSize: "1.2rem",
    },
  };
});

const StyledForm = styled(FormikForm)(({ theme }: IStyledArguments) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(3),
}));

const FormSection = styled("div")(({ theme }: IStyledArguments) => {
  const { md } = theme.breakpoints.values;

  return {
    background: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.extra.borderColor}`,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(16, 21, 26, 0.08)",
      borderColor: theme.palette.primary.main,
    },
    [`@media (max-width: ${md}px)`]: {
      borderRadius: 12,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
    },
  };
});

const SectionTitle = styled("div")(({ theme }: IStyledArguments) => {
  return {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "1.1rem",
    "& svg": {
      fontSize: "1.5rem",
    },
  };
});

const WorkspaceChip = styled(Chip)(({ theme }: IStyledArguments) => {
  return {
    margin: theme.spacing(2, 0),
    padding: theme.spacing(1.5, 1),
    fontSize: "1rem",
    fontWeight: 600,
    background: theme.palette.extra.accentSoft,
    border: `1px solid ${theme.palette.extra.borderColor}`,
    "& .MuiChip-icon": {
      color: theme.palette.primary.main,
      fontSize: "1.5rem",
    },
  };
});

const VersionText = styled("div")(({ theme }: IStyledArguments) => {
  return {
    fontSize: "0.75rem",
    color: theme.palette.text.disabled,
    marginTop: theme.spacing(1),
    fontFamily: "monospace",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
  };
});

const FormButton = styled(Button)(({ theme }: IStyledArguments) => {
  const { md } = theme.breakpoints.values;

  return {
    background: theme.palette.primary.main,
    color: "#fff",
    padding: theme.spacing(1.5, 4),
    borderRadius: 12,
    fontWeight: 600,
    fontSize: "1rem",
    textTransform: "none",
    transition: "all 0.3s ease",
    "&:hover": {
      background: theme.palette.primary.dark,
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(16, 21, 26, 0.16)",
    },
    "&:disabled": {
      background: "rgba(0, 0, 0, 0.12)",
      transform: "none",
    },
    [`@media (max-width: ${md}px)`]: {
      width: "100%",
      padding: theme.spacing(1.25, 3),
    },
  };
});

const LogoutButton = styled(Button)(({ theme }: IStyledArguments) => {
  const { md } = theme.breakpoints.values;

  return {
    background: theme.palette.error.main,
    color: "#fff",
    padding: theme.spacing(1.5, 4),
    borderRadius: 12,
    fontWeight: 600,
    fontSize: "1rem",
    textTransform: "none",
    marginTop: theme.spacing(2),
    transition: "all 0.3s ease",
    "&:hover": {
      background: theme.palette.error.dark,
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(16, 21, 26, 0.16)",
    },
    [`@media (max-width: ${md}px)`]: {
      width: "100%",
      padding: theme.spacing(1.25, 3),
    },
  };
});

export const USER_QUERY = gql`
  query CurrentUser {
    currentUser {
      id
      name
      surname
      email
      role
      telephone
      workspace
      avatarUrl
    }
  }
`;

export const UPDATE_USER_PROFILE = gql`
  mutation UpdateProfile($name: String, $email: String, $surname: String) {
    updateProfile(input: { name: $name, email: $email, surname: $surname }) {
      name
      surname
    }
  }
`;

// interface IUserData {
//   currentUser: any;
// }

// const useStyles = makeStyles(theme => ({
//   root: {
//     minHeight: `calc(100vh - ${theme.layout.headerHeight + 112}px )` // Remove the Drawer Header height
//   }
// }));

const PageViewProfile = injectIntl<any>(({ intl }) => {
  const [showUpdateError, setShowUpdateError] = useState(false);
  const [showUpdateSuccess, setShowUpdateSuccess] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>();
  const { data, error } = useQuery(USER_QUERY);
  const [, dispatch] = useAuthContext();

  const [updateUserProfile, { error: updateUserError }] = useMutation(
    UPDATE_USER_PROFILE
  );

  useEffect(() => {
    if (updateUserError) {
      setShowUpdateError(true);
    }
  }, [updateUserError]);

  useEffect(() => {
    if (data) {
      console.log("data------");
      console.log(data);
      setCurrentUser(data.currentUser);
    }
    if (error) {
      console.log("error loading user data", error);
    }
  }, [data]);

  return (
    <PageContainer>
      {currentUser && (
        <ContentCard>
          <ProfileHeader>
            <AvatarWrapper>
              <UserAvatarUploader
                user={{
                  name: (currentUser && currentUser.name) || "F",
                  surname: (currentUser && currentUser.surname) || "O",
                  email: (currentUser && currentUser.email) || "",
                  avatarUrl: (currentUser && currentUser.avatarUrl) || "",
                }}
              />
            </AvatarWrapper>
            <UserName>
              {currentUser.name} {currentUser.surname}
            </UserName>
            <UserEmail>
              <EmailIcon />
              {currentUser.email}
            </UserEmail>
            <WorkspaceChip
              icon={<BusinessIcon />}
              size="medium"
              variant="outlined"
              label={currentUser.workspace}
              onClick={
                useIsManager(currentUser.role) && currentUser.workspace
                  ? () => navigate(`/workspace/${currentUser.workspace}`)
                  : undefined
              }
              style={{ 
                cursor: useIsManager(currentUser.role) && currentUser.workspace ? 'pointer' : 'default',
                marginBottom: 16
              }}
            />
            {(() => {
              const isManager = useIsManager(currentUser.role);
              console.log('Debug Manage Workspace:', {
                role: currentUser.role,
                isManager,
                workspace: currentUser.workspace,
                shouldShow: isManager && currentUser.workspace
              });
              return isManager && currentUser.workspace && (
                <MuiButton
                  variant="outlined"
                  color="primary"
                  startIcon={<SettingsIcon />}
                  onClick={() => navigate(`/workspace/${currentUser.workspace}`)}
                  style={{ marginBottom: 16 }}
                >
                  Manage Workspace
                </MuiButton>
              );
            })()}
            <VersionText>
              v{import.meta.env.VITE_APP_VERSION || '0.1.2'}
            </VersionText>
          </ProfileHeader>

          <Formik
            initialValues={{
              name: (currentUser && currentUser.name) || "",
              surname: (currentUser && currentUser.surname) || "",
              email: (currentUser && currentUser.email) || "",
              role: (currentUser && currentUser.role) || "",
              password: "",
              passwordConfirmation: "",
            }}
            isInitialValid={true}
            onSubmit={(values: IFormValues) => {
              console.log("updating profile");
              console.log(currentUser.email);
              console.log(values.name);
              console.log(values.surname);
              updateUserProfile({
                variables: {
                  email: currentUser.email,
                  name: values.name,
                  surname: values.surname,
                },
              })
                .then(() => setShowUpdateSuccess(true))
                .catch(() => null);
            }}
            render={({ errors, dirty, isValid }: FormikProps<IFormValues>) => {
              return (
                <StyledForm data-testid="my-profile-details-form">
                  <FormSection>
                    <SectionTitle>
                      <AccountCircleIcon />
                      <span>Personal Information</span>
                    </SectionTitle>
                    <Field
                      name="name"
                      validate={required(intl)}
                      error={errors.name}
                      component={TextInput}
                      inputProps={{
                        "data-testid": "name",
                      }}
                      label={intl.formatMessage(messages.firstName)}
                    />
                    <InputErrorMessage name="name" />
                    <Field
                      name="surname"
                      validate={required(intl)}
                      error={errors.surname}
                      component={TextInput}
                      inputProps={{
                        "data-testid": "surname",
                      }}
                      label={intl.formatMessage(messages.lastName)}
                    />
                    <InputErrorMessage name="surname" />
                  </FormSection>

                  <FormSection>
                    <SectionTitle>
                      <EmailIcon />
                      <span>Account Details</span>
                    </SectionTitle>
                    <Field
                      name="email"
                      component={TextInput}
                      inputProps={{
                        "data-testid": "email",
                      }}
                      disabled
                      label={intl.formatMessage(messages.email)}
                    />
                  </FormSection>

                  <FormSection>
                    <SectionTitle>
                      <SecurityIcon />
                      <span>Role & Permissions</span>
                    </SectionTitle>
                    <Field
                      name="role"
                      component={TextInput}
                      inputProps={{
                        "data-testid": "role",
                      }}
                      disabled
                      label={intl.formatMessage(messages.role)}
                    />
                  </FormSection>

                  <Grid container spacing={2} style={{ marginTop: 8 }}>
                    <Grid item xs={12} sm="auto">
                      <FormButton
                        type="submit"
                        data-testid="update-button"
                        disabled={!isValid || !dirty}
                      >
                        {intl.formatMessage(messages.update)}
                      </FormButton>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} style={{ marginTop: 8 }}>
                    <Grid item xs={12}>
                      <LogoutButton
                        startIcon={<ExitToAppIcon />}
                        fullWidth
                        onClick={() => dispatch(actions.logout())}
                        data-testid="logout-button"
                      >
                        {intl.formatMessage(messages.logout || { id: "logout", defaultMessage: "Logout" })}
                      </LogoutButton>
                    </Grid>
                  </Grid>
                </StyledForm>
              );
            }}
          />
        </ContentCard>
      )}
      <SnackBar
        data-testid="snackbar-error-message"
        open={showUpdateError}
        message={intl.formatMessage(messages.updateUserError)}
        variant="error"
        onClose={
          // onClose is called by SnackBar after 6s
          /* istanbul ignore next */ () => setShowUpdateError(false)
        }
      />
      <SnackBar
        data-testid="snackbar-success-message"
        open={showUpdateSuccess}
        message={intl.formatMessage(messages.updateUserSuccess)}
        variant="success"
        onClose={
          // onClose is called by SnackBar after 6s
          /* istanbul ignore next */ () => setShowUpdateSuccess(false)
        }
      />
    </PageContainer>
  );
});

export default PageViewProfile;
